import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getIn, useFormik } from "formik";
import {
  clearTouchedListItem,
  removeTouchedListItem,
} from "../touchedListHelpers";

import messages from "../../../../../assets/locale/messages";
import useRevalidateOnLocaleChange from "../../../../../hooks/useRevalidateOnLocaleChange";
import {
  buildTouchedRoles,
  createRole,
  createShift,
  createTraining,
  firstTruthyBoundaryMeterString,
  getEventDayCount,
  hasAvailableShiftType,
  toNumber,
} from "./helpers";
import { hasMeaningfulPaidTrainingData } from "../../../../../utils/paidTrainingRemoval";
import getDefineRolesAndShiftsValidationSchema from "./validationSchema";
import {
  buildPortalRolesPayload,
  mapPortalEventToRolesValues,
} from "../../../../../utils/companyPortal/eventApi";
import {
  isPortalEventOngoing,
  isPortalEventOngoingOrUpcoming,
  isRoleRemovalLockedByEventTiming,
} from "../../../../../utils/companyPortal/eventEditRules";
import {
  showErrorMsg,
  showSuccessMsg,
  showWarningMsg,
} from "../../../../../utils/Helpers";
import { focusFirstError, getFirstErrorPath } from "../validationFocus";
import {
  CAPACITY_STATES,
  getCapacityState,
  getTotalApplicants,
} from "../ChooseCrewSize/crewSizeUtils";
import { EVENT_FORM_STEP_IDS } from "../../formStepIds";
import { TRAINING_VALIDATION_FIELDS } from "./defineRolesAndShiftsConstants";
import { isDraftEvent } from "../../../Event/BasicInfo/paymentStateUtils";
import { eventStatuses } from "../../../../../utils/Constants";
import {
  canListEventRequests,
  getRequestsListByPortal,
} from "../../../../../utils/requestApis";
import {
  getApplicantRoleId,
  getInvalidRoleIndexes,
  getRoleIndexFromPath,
  getRolesStepIssues,
  getShiftIndexFromPath,
  mergePersistedRoleIds,
} from "./defineRolesAndShiftsUtils";

export function useDefineRolesAndShifts({
  isEditMode,
  registerSubmit,
  registerStepApi,
  rolesOptions,
  eventScheduleBounds,
  initialEvent,
  requestsPortal,
  eventLocationBoundaryMeters,
  companyPortalMode,
  crewSizeOption,
  onGoToStep,
}) {
  const lang = useSelector((state) => state.locale.lang);
  const { events, inputsValidations, shared } = messages[lang];
  const t = events?.creatEvent?.defineRolesAndShifts;
  const creatEventLabels = events?.creatEvent?.labels;

  const event = useSelector((state) => state.events?.event);
  const login = useSelector((state) => state.auth?.login);
  const canListRequests = canListEventRequests(requestsPortal, login);

  const [activeRoleIndex, setActiveRoleIndex] = useState(0);
  const [activeShiftByRole, setActiveShiftByRole] = useState({});
  const [rolePendingRemoval, setRolePendingRemoval] = useState(null);
  const [shiftPendingRemoval, setShiftPendingRemoval] = useState(
    /** @type {{ roleIndex: number, shiftIndex: number } | null} */ (null)
  );
  const [paidTrainingRemoval, setPaidTrainingRemoval] = useState(
    /** @type {{ roleIndex: number, shiftIndex: number } | null} */ (null)
  );
  const [isRoleAccordionExpanded, setIsRoleAccordionExpanded] = useState(false);
  const [validationAttempt, setValidationAttempt] = useState(0);
  // Roles that existed at the last continue attempt; roles added afterwards
  // stay error-free until they're touched or the next attempt (PROLINE-2514).
  const [validatedRoleCount, setValidatedRoleCount] = useState(0);
  const [invalidRoleIndexesOverride, setInvalidRoleIndexesOverride] = useState(
    /** @type {number[]} */ ([])
  );
  const [emptyRolesAttention, setEmptyRolesAttention] = useState(0);
  const [capacityAttention, setCapacityAttention] = useState(0);
  const [applicantsCountByRoleId, setApplicantsCountByRoleId] = useState({});
  const emptyRolesRef = useRef(null);

  /**
   * Tracks server-persisted roles removed in this session.
   * Merged into every buildPayload call as `{ id, _destroy: true }` rows
   * so the backend deletes them on the next PATCH.
   */
  const deletedRolesRef = useRef([]);
  const deletedShiftsRef = useRef([]);

  const scheduleSourceForDays = useMemo(() => {
    if (
      eventScheduleBounds?.eventStartAt != null &&
      eventScheduleBounds?.eventEndAt != null
    ) {
      return {
        event_start_at: eventScheduleBounds.eventStartAt,
        event_end_at: eventScheduleBounds.eventEndAt,
      };
    }
    return event;
  }, [eventScheduleBounds, event]);

  const eventDayCount = useMemo(
    () => getEventDayCount(scheduleSourceForDays),
    [scheduleSourceForDays]
  );
  const eventBounds = useMemo(() => {
    if (
      eventScheduleBounds?.eventStartAt != null &&
      eventScheduleBounds?.eventEndAt != null
    ) {
      return {
        eventStartAt: eventScheduleBounds.eventStartAt,
        eventEndAt: eventScheduleBounds.eventEndAt,
      };
    }

    const ev = event || {};
    return {
      eventStartAt: ev.event_start_at || ev.actualStart || ev.actual_start_at,
      eventEndAt: ev.event_end_at || ev.actualEnd || ev.actual_end_at,
    };
  }, [event, eventScheduleBounds]);

  const validationMessages = useMemo(
    () => ({
      required: inputsValidations.generalFieldRequired,
      roleRequired: inputsValidations.eventRoleRequired,
      supervisorRoleRequired: inputsValidations.eventSupervisorRoleRequired,
      roleRequiredTitle: inputsValidations.eventRoleRequiredTitle,
      supervisorRoleRequiredTitle: inputsValidations.eventSupervisorRoleRequiredTitle,
      supervisorToSubmitHint: inputsValidations.eventSupervisorToSubmitHint,
      secondRoleToSubmitHint: inputsValidations.eventSecondRoleToSubmitHint,
      shiftRequired: inputsValidations.eventShiftRequired,
      capacityRequired: inputsValidations.eventRoleCapacityRequired,
      nonNegative: inputsValidations.onlyPositiveNumbersWithZero,
      positiveInteger: inputsValidations.onlyPositiveNumber,
      maxLength: inputsValidations.maxTowHundredChars,
      trainingPaidAmountInvalid: inputsValidations.trainingPaidAmountInvalid,
      pastDateTime: inputsValidations.pastDateTime,
      trainingStartMustBeBeforeEventEnd:
        inputsValidations.trainingStartMustBeBeforeEventEnd,
      trainingEndBeforeStart: inputsValidations.trainingEndBeforeStart,
      trainingEndTimeBeforeStartTime:
        inputsValidations.trainingEndTimeBeforeStartTime,
      trainingEndMustBeBeforeEventEnd:
        inputsValidations.trainingEndMustBeBeforeEventEnd,
      trainingConflictWithShift: inputsValidations.trainingConflictWithShift,
      trainingLocationInvalid: inputsValidations.trainingLocationInvalid,
      checkinStartMarginMax: inputsValidations.checkinStartMarginMax,
      checkinEndMarginMax: inputsValidations.checkinEndMarginMax,
      checkinMarginsTotalMax: inputsValidations.checkinMarginsTotalMax,
      checkinMarginsTotalMin: inputsValidations.checkinMarginsTotalMin,
      checkoutStartMarginMax: inputsValidations.checkoutStartMarginMax,
      checkoutEndMarginMax: inputsValidations.checkoutEndMarginMax,
      checkoutMarginsTotalMax: inputsValidations.checkoutMarginsTotalMax,
      checkoutMarginsTotalMin: inputsValidations.checkoutMarginsTotalMin,
      marginsOverlapWithSlot: inputsValidations.marginsOverlapWithSlot,
    }),
    [lang, inputsValidations]
  );
  const roleRequiredMessageRef = useRef(validationMessages.roleRequired);
  roleRequiredMessageRef.current = validationMessages.roleRequired;
  const supervisorRoleRequiredMessageRef = useRef(
    validationMessages.supervisorRoleRequired
  );
  supervisorRoleRequiredMessageRef.current =
    validationMessages.supervisorRoleRequired;
  const rolesToSubmitHintRef = useRef(validationMessages);
  rolesToSubmitHintRef.current = validationMessages;
  // A company draft can be saved without 2 roles with a Supervisor; the form's check stops its
  // Next and lists what's missing. A published event is refused by the backend without them,
  // so this step stops it.
  const rolesHintOnly =
    companyPortalMode && (!initialEvent?.status || isDraftEvent(initialEvent));

  /** Fixed system role — identified by its locale-independent `name_en`, like elsewhere in the app. */
  const supervisorRoleIds = useMemo(
    () =>
      new Set(
        (rolesOptions || [])
          .filter(
            (option) => (option?.name_en ?? option?.name) === "Supervisor"
          )
          .map((option) => String(option.id))
      ),
    [rolesOptions]
  );

  const validationSchema = useMemo(
    () =>
      getDefineRolesAndShiftsValidationSchema(validationMessages, eventBounds),
    [validationMessages, eventBounds]
  );

  const formInitialValues = useMemo(
    () =>
      initialEvent
        ? mapPortalEventToRolesValues(initialEvent)
        : { event_roles_attributes: [] },
    [initialEvent]
  );

  const formik = useFormik({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: () => {},
  });

  const portalCompanyOngoingEdit = useMemo(
    () =>
      Boolean(
        companyPortalMode &&
          isEditMode &&
          initialEvent?.status &&
          isPortalEventOngoing(initialEvent.status),
      ),
    [companyPortalMode, isEditMode, initialEvent?.status],
  );
  const portalCompanyStaffingRemovalRestricted = useMemo(
    () =>
      Boolean(
        companyPortalMode &&
          isEditMode &&
          initialEvent?.status &&
          isPortalEventOngoingOrUpcoming(initialEvent.status),
      ),
    [companyPortalMode, isEditMode, initialEvent?.status],
  );
  const roleRemovalLockedNearEventStart = useMemo(() => {
    if (!isEditMode) return false;
    // Newly added (unsaved) roles have no server id — bypass the timing lock so
    // they can always be removed without the "ongoing event" confirmation block.
    if (rolePendingRemoval != null) {
      const pendingRole =
        formik.values.event_roles_attributes?.[rolePendingRemoval];
      if (pendingRole && !pendingRole.id) return false;
    }
    const eventStartAt = eventBounds?.eventStartAt;
    return isRoleRemovalLockedByEventTiming(initialEvent?.status, eventStartAt);
  }, [eventBounds, formik.values.event_roles_attributes, initialEvent, isEditMode, rolePendingRemoval]);

  useEffect(() => {
    const eventId = initialEvent?.id;
    if (
      !isEditMode ||
      !eventId ||
      !portalCompanyStaffingRemovalRestricted ||
      !canListRequests
    ) {
      setApplicantsCountByRoleId({});
      return undefined;
    }

    let cancelled = false;

    const loadRoleApplicants = async () => {
      try {
        const getRequestsList = getRequestsListByPortal(requestsPortal);
        const response = await getRequestsList({
          page: 1,
          items: 1000,
          event_id: eventId,
          status: "approved",
          order_by_created_at: "asc",
        });
        const applicants = response?.data?.data || [];
        const nextCounts = applicants.reduce((acc, applicant) => {
          const roleId = getApplicantRoleId(applicant);
          if (roleId == null || roleId === "") return acc;
          const key = String(roleId);
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        if (cancelled) return;

        setApplicantsCountByRoleId(nextCounts);
      } catch (error) {
        if (!cancelled) setApplicantsCountByRoleId({});
      }
    };

    void loadRoleApplicants();

    return () => {
      cancelled = true;
    };
  }, [
    initialEvent?.id,
    isEditMode,
    portalCompanyStaffingRemovalRestricted,
    requestsPortal,
    canListRequests,
  ]);

  /**
   * After first validation attempt, keep sidebar red cards in sync with live
   * formik errors so fixing a field immediately removes the red indicator.
   */
  const invalidRoleIndexes = useMemo(() => {
    if (validationAttempt === 0) return invalidRoleIndexesOverride;
    return getInvalidRoleIndexes(
      formik.errors.event_roles_attributes,
      validatedRoleCount
    );
  }, [
    formik.errors,
    validationAttempt,
    invalidRoleIndexesOverride,
    validatedRoleCount,
  ]);

  const restoredEventIdRef = useRef(null);
  useEffect(() => {
    const restoredEventId = initialEvent?.id;
    if (!restoredEventId || restoredEventIdRef.current === restoredEventId) {
      return;
    }

    const nextValues = mapPortalEventToRolesValues(initialEvent);
    restoredEventIdRef.current = restoredEventId;
    formik.setValues(nextValues, false);
    formik.setTouched({}, false);
    deletedRolesRef.current = [];
    deletedShiftsRef.current = [];
    setInvalidRoleIndexesOverride([]);
    setValidationAttempt(0);
    setValidatedRoleCount(0);
    setEmptyRolesAttention(0);
    setActiveRoleIndex(0);
    setActiveShiftByRole({});
  }, [initialEvent]);

  useRevalidateOnLocaleChange(() => {
    void formik.validateForm();
  });

  const roles = formik.values.event_roles_attributes;

  /**
   * Crew size capacity counter. The ceiling is enforced when the form has a Crew Size step
   * to go back to (`onGoToStep`), or when the event waits on approval and its crew size can't
   * change (PROLINE-4160); otherwise it is display-only.
   */
  const capacity = crewSizeOption
    ? {
        used: getTotalApplicants(roles),
        ceiling: crewSizeOption.max,
        crewSizeLabel:
          crewSizeOption.name ??
          events?.creatEvent?.crewSize?.sizes?.[crewSizeOption.key] ??
          "",
        onGoToCrewSize: onGoToStep
          ? () => onGoToStep(EVENT_FORM_STEP_IDS.crewSize)
          : undefined,
        crewSizeLocked: initialEvent?.status === eventStatuses.pendingPayment,
        attentionKey: capacityAttention,
      }
    : null;
  const isCapacityEnforced = Boolean(
    capacity?.onGoToCrewSize || capacity?.crewSizeLocked,
  );
  const isAboveCapacity =
    isCapacityEnforced &&
    getCapacityState(capacity.used, capacity.ceiling) === CAPACITY_STATES.above;
  const isAboveCapacityRef = useRef(isAboveCapacity);
  isAboveCapacityRef.current = isAboveCapacity;

  /**
   * What Next and Submit for Approval still need from this step: 2 roles with a Supervisor,
   * and a total within the crew size. The form's check lists them in its banner.
   */
  const getStepIssues = () =>
    getRolesStepIssues({
      roles: formik.values.event_roles_attributes,
      supervisorRoleIds,
      ceiling: isCapacityEnforced ? capacity.ceiling : null,
      hints: {
        secondRole: rolesToSubmitHintRef.current.secondRoleToSubmitHint,
        supervisor: rolesToSubmitHintRef.current.supervisorToSubmitHint,
        exceeded:
          events?.creatEvent?.defineRolesAndShifts?.[
            capacity?.crewSizeLocked ? "exceededCapacityLocked" : "exceededCapacity"
          ],
      },
    });

  const activeRole = roles[activeRoleIndex];
  const activeShiftIndex = activeShiftByRole[activeRoleIndex] || 0;

  const setRoles = (nextRoles) => {
    formik.setFieldValue("event_roles_attributes", nextRoles);
  };

  const handleAddRole = () => {
    const nextRoles = [...roles, createRole()];
    setEmptyRolesAttention(0);
    setRoles(nextRoles);
    formik.setTouched(
      clearTouchedListItem(
        formik.touched,
        "event_roles_attributes",
        nextRoles.length - 1
      ),
      false
    );
    setActiveRoleIndex(nextRoles.length - 1);
    setActiveShiftByRole((current) => ({
      ...current,
      [nextRoles.length - 1]: 0,
    }));
    setIsRoleAccordionExpanded(true);
  };

  const roleHasApplicants = (role) =>
    portalCompanyStaffingRemovalRestricted &&
    (Number(role?.assigned_applicants_count || 0) > 0 ||
      Number(applicantsCountByRoleId[String(role?.role_id)] || 0) > 0);

  const handleRequestRemoveRole = () => {
    if (roleHasApplicants(activeRole)) {
      showErrorMsg(t.removeRoleHasApplicants, true);
      return;
    }
    setRolePendingRemoval(activeRoleIndex);
  };

  const handleRemoveRole = () => {
    if (rolePendingRemoval === null) return;

    const removedRole = roles[rolePendingRemoval];

    const persistedRole = removedRole?.id != null && removedRole.id !== "";

    // If this role was already saved on the server, queue it for _destroy on next PATCH.
    if (persistedRole) {
      deletedRolesRef.current = [
        ...deletedRolesRef.current,
        { id: removedRole.id, _destroy: true },
      ];
    }

    const nextRoles = roles.filter(
      (_role, index) => index !== rolePendingRemoval
    );
    setRoles(nextRoles);
    formik.setTouched(
      removeTouchedListItem(
        formik.touched,
        "event_roles_attributes",
        rolePendingRemoval
      ),
      false
    );
    if (rolePendingRemoval < validatedRoleCount) {
      setValidatedRoleCount((count) => count - 1);
    }
    setActiveRoleIndex((current) => {
      if (!nextRoles.length) return 0;
      if (rolePendingRemoval < current) return current - 1;
      return Math.min(current, nextRoles.length - 1);
    });
    setActiveShiftByRole({});
    if (!nextRoles.length) {
      setEmptyRolesAttention(0);
    }
    setRolePendingRemoval(null);
    if (persistedRole) {
      showWarningMsg(t.roleRemovalPendingSave);
    } else {
      showSuccessMsg(t.roleRemoved, true);
    }
  };

  const handleAddShift = () => {
    if (
      !activeRole ||
      !hasAvailableShiftType(activeRole.event_role_shifts_attributes)
    ) {
      return;
    }

    const nextRoles = roles.map((role, roleIndex) =>
      roleIndex === activeRoleIndex
        ? {
            ...role,
            event_role_shifts_attributes: [
              ...role.event_role_shifts_attributes,
              createShift(),
            ],
          }
        : role
    );
    const nextShiftIndex = activeRole.event_role_shifts_attributes.length;

    setRoles(nextRoles);
    setActiveShiftByRole((current) => ({
      ...current,
      [activeRoleIndex]: nextShiftIndex,
    }));
  };

  const handleRemoveShift = () => {
    if (!shiftPendingRemoval) return;

    const { roleIndex: pendingRoleIndex, shiftIndex: pendingShiftIndex } =
      shiftPendingRemoval;
    const pendingRole = roles[pendingRoleIndex];
    if (!pendingRole || pendingRole.event_role_shifts_attributes.length <= 1) {
      setShiftPendingRemoval(null);
      return;
    }

    const removedShift =
      pendingRole.event_role_shifts_attributes[pendingShiftIndex];

    if (removedShift?.id != null && removedShift.id !== "") {
      deletedShiftsRef.current = [
        ...deletedShiftsRef.current,
        {
          roleId: pendingRole.id,
          roleIndex: pendingRoleIndex,
          shift: { id: removedShift.id, _destroy: true },
        },
      ];
    }

    const nextRoles = roles.map((role, roleIndex) => {
      if (roleIndex !== pendingRoleIndex) return role;

      return {
        ...role,
        event_role_shifts_attributes: role.event_role_shifts_attributes.filter(
          (_shift, shiftIndex) => shiftIndex !== pendingShiftIndex
        ),
      };
    });

    setRoles(nextRoles);
    setActiveShiftByRole((current) => ({
      ...current,
      [pendingRoleIndex]: Math.max(0, pendingShiftIndex - 1),
    }));
    setShiftPendingRemoval(null);
    showSuccessMsg(t.shiftRemoved, true);
  };

  const handleShiftChange = (nextShiftIndex) => {
    setActiveShiftByRole((current) => ({
      ...current,
      [activeRoleIndex]: nextShiftIndex,
    }));
  };

  const clearTrainingValidationState = (path) => {
    TRAINING_VALIDATION_FIELDS.forEach((field) => {
      const fieldPath = `${path}.${field}`;
      formik.setFieldTouched(fieldPath, false, false);
      formik.setFieldError(fieldPath, undefined);
    });
  };

  const handleConfirmRemovePaidTraining = () => {
    if (!paidTrainingRemoval) return;
    const { roleIndex, shiftIndex } = paidTrainingRemoval;
    const path = `event_roles_attributes.${roleIndex}.event_role_shifts_attributes.${shiftIndex}.training_attributes`;
    clearTrainingValidationState(path);
    const trainingId = getIn(formik.values, path)?.id;
    formik.setFieldValue(
      path,
      trainingId != null && trainingId !== ""
        ? { id: trainingId, _destroy: true }
        : null
    );
    setPaidTrainingRemoval(null);
  };

  const handleTrainingToggle = (checked) => {
    const roleIndex = activeRoleIndex;
    const shiftIndex = activeShiftIndex;
    const path = `event_roles_attributes.${roleIndex}.event_role_shifts_attributes.${shiftIndex}.training_attributes`;
    const training = getIn(formik.values, path);

    if (!checked) {
      if (isEditMode && hasMeaningfulPaidTrainingData(training)) {
        setPaidTrainingRemoval({ roleIndex, shiftIndex });
        return;
      }
    }

    clearTrainingValidationState(path);

    if (!checked) {
      formik.setFieldValue(path, null);
      return;
    }

    const seed = firstTruthyBoundaryMeterString(
      eventLocationBoundaryMeters,
      event?.event_radius
    );
    const trainingPayload = createTraining();
    if (seed) {
      trainingPayload.location_radius = seed;
    }

    formik.setFieldValue(path, trainingPayload);
  };

  const resetValidationState = () => {
    setInvalidRoleIndexesOverride([]);
    setValidationAttempt(0);
    setValidatedRoleCount(0);
    setEmptyRolesAttention(0);
  };

  const applyPersistedEvent = (attrs) => {
    if (!attrs) return;
    formik.setFieldValue(
      "event_roles_attributes",
      mergePersistedRoleIds(formik.values.event_roles_attributes, attrs),
      true
    );
  };

  useEffect(() => {
    const focusRoleError = (errorPath) => {
      const firstRoleIndex = getRoleIndexFromPath(errorPath);
      const firstShiftIndex = getShiftIndexFromPath(errorPath);

      if (firstRoleIndex !== null) {
        setInvalidRoleIndexesOverride((current) =>
          current.includes(firstRoleIndex)
            ? current
            : [...current, firstRoleIndex]
        );
        setActiveRoleIndex(firstRoleIndex);
        setIsRoleAccordionExpanded(true);
        if (firstShiftIndex !== null) {
          setActiveShiftByRole((current) => ({
            ...current,
            [firstRoleIndex]: firstShiftIndex,
          }));
        }
      }

      setValidationAttempt((current) => current + 1);
      focusFirstError(errorPath, {
        sectionSelector:
          ".shift-panel--error, .role-editor-accordion--error, .form-section--error",
        delay: 160,
      });
    };

    const validateContinue = async () => {
      // Above the crew size ceiling: point at the counter. The form's check (`countMissingFields`)
      // stops Next and Submit for Approval, and its banner says why.
      if (isAboveCapacityRef.current) {
        setCapacityAttention((current) => current + 1);
      }

      if (!formik.values.event_roles_attributes.length) {
        showErrorMsg(
          roleRequiredMessageRef.current,
          rolesToSubmitHintRef.current.roleRequiredTitle
        );
        setEmptyRolesAttention((current) => current + 1);
        emptyRolesRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        window.setTimeout(() => {
          emptyRolesRef.current?.querySelector("button")?.focus();
        }, 250);
        formik.setTouched(
          buildTouchedRoles(formik.values.event_roles_attributes),
          false
        );
        return false;
      }

      const hasSupervisorRole =
        supervisorRoleIds.size === 0 ||
        formik.values.event_roles_attributes.some((role) =>
          supervisorRoleIds.has(String(role?.role_id))
        );
      // A company draft is stopped by the form's check on Next instead, so it can still be saved.
      if (!rolesHintOnly && !hasSupervisorRole) {
        showErrorMsg(
          supervisorRoleRequiredMessageRef.current,
          rolesToSubmitHintRef.current.supervisorRoleRequiredTitle
        );
        return false;
      }

      const errors = await formik.validateForm();
      setValidatedRoleCount(formik.values.event_roles_attributes.length);
      const roleErrors = errors.event_roles_attributes;
      const nextInvalidRoleIndexes = Array.isArray(roleErrors)
        ? roleErrors
            .map((roleError, index) => (roleError ? index : null))
            .filter((index) => index !== null)
        : [];

      setInvalidRoleIndexesOverride(nextInvalidRoleIndexes);
      if (nextInvalidRoleIndexes.length) {
        const firstErrorPath = getFirstErrorPath(errors);
        const firstRoleIndex =
          getRoleIndexFromPath(firstErrorPath) ?? nextInvalidRoleIndexes[0];
        focusRoleError(
          firstErrorPath || `event_roles_attributes.${firstRoleIndex}`
        );
      }

      formik.setTouched(
        buildTouchedRoles(formik.values.event_roles_attributes),
        false
      );
      // A company draft missing 2 roles with a Supervisor passes here; the form's check on Next
      // (`countMissingFields`) stops it and its banner says what's missing.
      return Object.keys(errors).length === 0;
    };

    if (registerStepApi) {
      registerStepApi({
        validateContinue,
        // Silent check for Next and Submit for Approval: only the step's overall rules (2 roles
        // with a Supervisor, a total within the crew size). Empty fields of a role being added
        // aren't counted: Save and Next already refuse them.
        countMissingFields: async () => getStepIssues().length,
        // What the banner and the stepper spell out for this step, one line per rule.
        getSubmitIssues: () => getStepIssues(),
        validatePortalDraft: companyPortalMode
          ? async () => {
              // The backend refuses a draft above the crew size ceiling: point at the counter instead.
              if (isAboveCapacityRef.current) {
                setCapacityAttention((current) => current + 1);
                return false;
              }
              // Same as Next: the backend refuses a draft with missing required fields.
              return validateContinue();
            }
          : undefined,
        buildPayload: (options) => {
          const base = buildPortalRolesPayload(
            formik.values,
            eventBounds,
            options
          );
          const eventRolesWithDeletedShifts = base.event_roles_attributes.map(
            (role, roleIndex) => {
              const deletedShifts = deletedShiftsRef.current
                .filter((item) =>
                  item.roleId != null && role.id != null
                    ? String(item.roleId) === String(role.id)
                    : item.roleIndex === roleIndex
                )
                .map((item) => item.shift);

              if (!deletedShifts.length) return role;

              return {
                ...role,
                event_role_shifts_attributes: [
                  ...(role.event_role_shifts_attributes || []),
                  ...deletedShifts,
                ],
              };
            }
          );

          return {
            ...base,
            event_roles_attributes: [
              ...eventRolesWithDeletedShifts,
              ...deletedRolesRef.current,
            ],
          };
        },
        applyPersistedEvent,
        resetValidationState,
      });
      return () => registerStepApi(null);
    }

    registerSubmit?.(async () => validateContinue());

    return () => registerSubmit?.(null);
  }, [
    formik,
    registerStepApi,
    registerSubmit,
    eventBounds,
    companyPortalMode,
    validationMessages,
    supervisorRoleIds,
    rolesHintOnly,
  ]);

  useEffect(() => {
    roles.forEach((role, roleIndex) => {
      role.event_role_shifts_attributes.forEach((shift, shiftIndex) => {
        const payroll = toNumber(shift.payroll_amount);
        const payrollPerDay =
          eventDayCount > 0 && payroll > 0
            ? (payroll / eventDayCount).toFixed(2)
            : "";
        const path = `event_roles_attributes.${roleIndex}.event_role_shifts_attributes.${shiftIndex}.payroll_per_day`;

        if (shift.payroll_per_day !== payrollPerDay) {
          formik.setFieldValue(path, payrollPerDay, false);
        }
      });
    });
  }, [eventDayCount, roles]);

  return {
    formik,
    t,
    creatEventLabels,
    shared,
    event,
    emptyRolesRef,
    roles,
    rolesOptions,
    activeRoleIndex,
    activeRole,
    activeShiftIndex,
    invalidRoleIndexes,
    validationAttempt,
    activeRoleValidated: activeRoleIndex < validatedRoleCount,
    emptyRolesAttention,
    isRoleAccordionExpanded,
    setActiveRoleIndex,
    setIsRoleAccordionExpanded,
    rolePendingRemoval,
    setRolePendingRemoval,
    shiftPendingRemoval,
    setShiftPendingRemoval,
    paidTrainingRemoval,
    setPaidTrainingRemoval,
    eventLocationBoundaryMeters,
    portalCompanyOngoingEdit,
    companyPortalMode,
    isEditMode,
    roleRemovalLockedNearEventStart,
    handleAddRole,
    handleRequestRemoveRole,
    handleRemoveRole,
    handleAddShift,
    handleRemoveShift,
    handleShiftChange,
    handleTrainingToggle,
    handleConfirmRemovePaidTraining,
    roleHasApplicants,
    capacity,
  };
}
